import React, { useState } from 'react'

import {
  Box,
  CloseButton,
  Combobox,
  Group,
  InputBase,
  ScrollArea,
  Typography,
  useCombobox,
  useMantineTheme
} from '@tidbcloud/uikit'
import { IconCheck } from '@tidbcloud/uikit/icons'

export type FilterMultiSelectProps = {
  kind: string
  data: { label: string; value: string }[]
  value: string[]
  onChange: (value: string[]) => void

  width?: number
  disabled?: boolean
  error?: string
  label?: string
  maxDisplayItem?: number
}

export function FilterMultiSelect({
  kind,
  data,
  value,
  onChange,
  width,
  disabled,
  error,
  label,
  maxDisplayItem
}: FilterMultiSelectProps) {
  const theme = useMantineTheme()

  const [search, setSearch] = useState('')
  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption()
      // combobox.focusTarget()
      setSearch('')
    },

    onDropdownOpen: () => {
      combobox.focusSearchInput()
    }
  })

  const options = data
    .filter((item) =>
      item.label.toLowerCase().includes(search.toLowerCase().trim())
    )
    .map((item) => (
      <Combobox.Option
        value={item.value}
        key={item.value}
        styles={{
          option: {
            '&:hover': {
              backgroundColor: theme.colors.carbon[3]
            }
          }
        }}
      >
        <Group>
          {item.label}
          <Group ml='auto'>
            {value.includes(item.value) && (
              <IconCheck
                size={16}
                strokeWidth={2}
                color={theme.colors.peacock[7]}
              />
            )}
          </Group>
        </Group>
      </Combobox.Option>
    ))

  function handleOptionSelect(val: string) {
    const newValue = value.includes(val)
      ? value.filter((v) => v !== val)
      : [...value, val]
    onChange(newValue)
  }

  function selectResult() {
    const maxItemLength = maxDisplayItem || 2
    if (value.length === 0) {
      return <Typography c='carbon' truncate>{`Select ${kind}...`}</Typography>
      // return <Input.Placeholder>Select Resource Groups...</Input.Placeholder>
    }
    if (value.length < maxItemLength) {
      return (
        <Group gap={4} wrap='nowrap'>
          <Typography c='carbon' fw={500} truncate>
            {kind}
          </Typography>
          <Typography fw={500} truncate>
            {value
              .map((val) => data.find((v) => v.value === val)?.label)
              .join(', ')}
          </Typography>
        </Group>
      )
    }
    return (
      <Group gap={4} wrap='nowrap'>
        <Typography c='carbon' fw={500} truncate>
          {kind}
        </Typography>
        <Typography fw={500} truncate>
          {value.length} selected
        </Typography>
      </Group>
    )
  }

  return (
    <Combobox
      store={combobox}
      withinPortal={true}
      onOptionSubmit={handleOptionSelect}
      shadow='sm'
      styles={{
        search: {
          border: 'none',
          borderBottom: `1px solid ${theme.colors.carbon[3]}`,
          '&:hover': {
            borderBottom: `1px solid ${theme.colors.carbon[3]}`
          },
          '&:focus': {
            borderBottom: `1px solid ${theme.colors.carbon[3]}`
          }
        }
      }}
    >
      <Combobox.Target>
        <InputBase
          w={width}
          disabled={disabled}
          component='button'
          type='button'
          pointer
          rightSection={
            value.length > 0 ? (
              <CloseButton
                size='sm'
                onMouseDown={(event: React.MouseEvent<HTMLButtonElement>) =>
                  event.preventDefault()
                }
                onClick={() => onChange([])}
                aria-label='Clear value'
              />
            ) : (
              <Combobox.Chevron />
            )
          }
          label={
            label ? (
              <Box sx={(t) => ({ color: t.colors.carbon[8] })} mb='6'>
                {label}
              </Box>
            ) : undefined
          }
          error={
            error ? (
              <Box sx={(t) => ({ color: t.colors.red[7] })}>{error}</Box>
            ) : undefined
          }
          onClick={() => combobox.toggleDropdown()}
          rightSectionPointerEvents={value === null ? 'none' : 'all'}
        >
          {selectResult()}
        </InputBase>
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Search
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
          placeholder={`Search ${kind.toLowerCase()}`}
          styles={{
            input: {
              border: 'none'
            }
          }}
        />
        <ScrollArea.Autosize type='scroll' mah={200}>
          <Combobox.Options>
            {options.length > 0 ? (
              options
            ) : (
              <Combobox.Empty>Nothing found</Combobox.Empty>
            )}
          </Combobox.Options>
        </ScrollArea.Autosize>
      </Combobox.Dropdown>
    </Combobox>
  )
}
